import { Link, useLocation } from "@remix-run/react";
import { clsx } from "clsx";
import * as React from "react";

import { Button } from "~/components/button";
import { Container } from "~/components/container";
import { Footer } from "~/components/footer";

import { Svg } from "./svg";

const navigation = [
  { title: "How we deliver", href: "/#delivery" },
  { title: "About", href: "/about" },
  { title: "Start a project", href: "/contact" },
];

function RootLayoutInner({ children }: { children: React.ReactNode }) {
  let panelId = React.useId();
  let [expanded, setExpanded] = React.useState(false);

  return (
    <>
      <header className="relative z-40 border-b border-ink-800 bg-ink-950">
        <Container>
          <div className="flex items-center justify-between py-6">
            <Link to="/" aria-label="Home" className="text-ink-50">
              <Svg name="logo-full" className="h-8 w-36" />
            </Link>

            <div className="flex items-center gap-x-4 sm:gap-x-8">
              <nav className="hidden items-center gap-x-8 lg:flex">
                {navigation.slice(0, 2).map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="label text-ink-300 transition hover:text-ink-50"
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>

              <Button to="/contact">Book a call</Button>

              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded ? "true" : "false"}
                aria-controls={panelId}
                aria-label="Toggle navigation"
                className="group -m-2.5 rounded-full p-2.5 transition hover:bg-ink-50/10 lg:hidden"
              >
                <Svg
                  name={expanded ? "x" : "menu"}
                  className="h-6 w-6 fill-ink-50 group-hover:fill-white"
                />
              </button>
            </div>
          </div>
        </Container>

        <nav
          id={panelId}
          className={clsx(
            "border-t border-ink-800 lg:hidden",
            !expanded && "hidden",
          )}
        >
          <Container>
            <ul className="py-2">
              {navigation.map((item) => (
                <li
                  key={item.href}
                  className="border-b border-ink-800 last:border-0"
                >
                  <Link
                    to={item.href}
                    onClick={() => setExpanded(false)}
                    className="block py-5 font-display text-2xl font-medium tracking-tight text-ink-50 transition hover:text-accent-300"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </Container>
        </nav>
      </header>

      <div className="relative flex flex-auto flex-col bg-ink-950">
        <main className="w-full flex-auto">{children}</main>
        <Footer />
      </div>
    </>
  );
}

export function RootLayout({ children }: { children: React.ReactNode }) {
  let { pathname } = useLocation();
  return <RootLayoutInner key={pathname}>{children}</RootLayoutInner>;
}
